import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { LazyVideo } from 'components/common/LazyVideo';
import PageLayout from 'components/common/PageLayout';
import { API_ROUTE, ENTERPRISE_CONTACT_LINK, STUDIO_ROUTE } from 'components/constants';
import useResponsiveQuery from 'utils/useResponsiveQuery';

import styles from './analytics.module.scss';

type CountUpProps = {
  active: boolean;
  value: number;
  delay: number;
  decimals?: number;
  suffix?: string;
  formatter?: (value: number) => string;
};

const CountUp: React.FC<CountUpProps> = ({ active, value, delay, decimals = 0, suffix = '', formatter }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayValue(value);
      return undefined;
    }

    let animationFrame = 0;
    const timeout = window.setTimeout(() => {
      const startedAt = performance.now();
      const duration = 720;

      const update = (now: number) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(value * easedProgress);

        if (progress < 1) animationFrame = window.requestAnimationFrame(update);
      };

      animationFrame = window.requestAnimationFrame(update);
    }, delay);

    return () => {
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [active, delay, value]);

  const formattedValue = formatter
    ? formatter(displayValue)
    : displayValue.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return <>{formattedValue}{suffix}</>;
};

const formatDuration = (value: number) => {
  const seconds = Math.round(value);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

const insightsMedia = {
  desktop: 'https://assts.stories.sc/website/desktop/insights.mp4',
  mobile: 'https://assts.stories.sc/website/mobile/insights.mp4',
  poster: 'https://assts.stories.sc/website/poster/insights.jpg',
};

const measurementPillars = [
  {
    number: '01',
    title: 'Reach and attention',
    text: 'Track views, time spent, video engagement and the products or screens that hold attention.',
  },
  {
    number: '02',
    title: 'Choices and intent',
    text: 'Measure answers, clicks, form submissions and custom events that show what each audience is trying to do.',
  },
  {
    number: '03',
    title: 'Journey performance',
    text: 'Compare starts, completions and lead submissions, then inspect the cards and steps where people leave.',
  },
  {
    number: '04',
    title: 'Audience context',
    text: 'Break results down by device, platform, location and traffic source to find meaningful differences in behavior.',
  },
];

const diagnosticSteps = [
  {
    number: '01',
    title: 'Start with the portfolio view',
    text: 'Review performance across the organization and identify the products, campaigns or markets that need attention.',
  },
  {
    number: '02',
    title: 'Find the exact point of friction',
    text: 'Move from a product into its cards, steps, answers and paths to see where behavior changes or completion drops.',
  },
  {
    number: '03',
    title: 'Change, compare and learn',
    text: 'Refine the flow or content in Studio, compare variations with A/B testing and measure the effect on the next audience.',
  },
];

const operatingCapabilities = [
  {
    title: 'Real-time visibility',
    text: 'See new views, answers, completions and leads as people interact, so teams can respond to performance changes quickly.',
  },
  {
    title: 'A/B testing insights',
    text: 'Compare variations and see how changes to content, design or flow affect completion and other meaningful outcomes.',
  },
  {
    title: 'Reports and connected data',
    text: 'Export the results your teams need or send selected interaction events into your analytics and data stack through the Storycards API.',
  },
  {
    title: 'Role-based analytics access',
    text: 'Give data and business teams access to dashboards and reports without giving them permission to edit or publish products.',
  },
];

const Analytics: React.FC = () => {
  const dashboardRef = useRef<HTMLElement>(null);
  const [dashboardActive, setDashboardActive] = useState(false);
  const isMobile = useResponsiveQuery();

  useEffect(() => {
    const dashboard = dashboardRef.current;
    if (!dashboard) return undefined;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDashboardActive(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setDashboardActive(true);
        observer.disconnect();
      },
      { threshold: 0.22 },
    );

    observer.observe(dashboard);
    return () => observer.disconnect();
  }, []);

  return (
    <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Interactive product analytics</p>
            <h1>See what people choose. Know what to improve.</h1>
            <p className={styles.heroText}>
              Measure views, answers, navigation, drop-off, completions and leads across every Storycards product.
              Move from an organization-wide view to a single card or choice, then use the evidence to improve the next
              version.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <div className={styles.heroPromise}>
            <span>Analytics built around the journey</span>
            <div>
              <strong>Go beyond page-level traffic</strong>
              <p>Understand screens, paths, answers and outcomes inside a complete interactive product.</p>
            </div>
            <div>
              <strong>Keep insight close to creation</strong>
              <p>Use what you learn to refine the experience in Studio and measure the next version.</p>
            </div>
          </div>
        </div>

        <figure
          ref={dashboardRef}
          className={`${styles.dashboard} ${dashboardActive ? styles.dashboardActive : ''}`}
          aria-labelledby="analytics-dashboard-caption"
        >
          <figcaption id="analytics-dashboard-caption" className={styles.dashboardCaption}>
            Illustrative Storycards analytics view with example data
          </figcaption>
          <div className={styles.dashboardTopbar}>
            <div>
              <span className={styles.liveDot} />
              Organization analytics
            </div>
            <span>Last 30 days</span>
          </div>
          <div className={styles.dashboardBody}>
            <div className={styles.metricGrid}>
              <article>
                <span>Views</span>
                <strong><CountUp active={dashboardActive} value={48320} delay={80} /></strong>
                <small>Across all products</small>
              </article>
              <article>
                <span>Completion rate</span>
                <strong><CountUp active={dashboardActive} value={72.4} delay={230} decimals={1} suffix="%" /></strong>
                <small>From start to finish</small>
              </article>
              <article>
                <span>Leads</span>
                <strong><CountUp active={dashboardActive} value={1248} delay={380} /></strong>
                <small>Submitted this period</small>
              </article>
              <article>
                <span>Time on page</span>
                <strong><CountUp active={dashboardActive} value={222} delay={530} formatter={formatDuration} /></strong>
                <small>Example average</small>
              </article>
            </div>

            <div className={styles.dashboardGrid}>
              <article className={styles.funnelPanel}>
                <header>
                  <div>
                    <span>Journey performance</span>
                    <strong>Product completion by step</strong>
                  </div>
                  <small>Example product</small>
                </header>
                <div className={styles.funnelChart} aria-hidden="true">
                  <div><span>Viewed</span><i style={{ width: '100%' }} /><b>100%</b></div>
                  <div><span>Started</span><i style={{ width: '82%' }} /><b>82%</b></div>
                  <div><span>Answered</span><i style={{ width: '68%' }} /><b>68%</b></div>
                  <div><span>Completed</span><i style={{ width: '54%' }} /><b>54%</b></div>
                </div>
              </article>

              <article className={styles.answerPanel}>
                <header>
                  <span>Answer distribution</span>
                  <strong>Card 06 · Preference</strong>
                </header>
                <div className={styles.donut} aria-hidden="true">
                  <span><CountUp active={dashboardActive} value={1904} delay={1580} /><small>answers</small></span>
                </div>
                <ul>
                  <li><span><i />Option A</span><b>46%</b></li>
                  <li><span><i />Option B</span><b>34%</b></li>
                  <li><span><i />Option C</span><b>20%</b></li>
                </ul>
              </article>
            </div>

            <div className={styles.dashboardFooter}>
              <span>Filter by product, card, step, device, source or region</span>
              <span>Example data</span>
            </div>
          </div>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>Analytics for the decisions inside the experience.</h2>
          <div>
            <p>
              Traditional web analytics can tell you that someone visited a page. Storycards analytics shows what
              happened after they arrived: which path they followed, what they chose, where they stopped and whether
              they completed the intended journey.
            </p>
            <p>
              That detail gives product, marketing, content and data teams a shared view of performance without
              reducing a multi-step experience to a single pageview.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.measurementSection}>
        <div className={styles.sectionInner}>
          <header className={styles.measurementHeader}>
            <p className={styles.eyebrow}>What you can measure</p>
            <h2>Read the whole journey, from first view to final outcome.</h2>
          </header>
          <div className={styles.measurementGrid}>
            {measurementPillars.map((item) => (
              <article key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.insightsDemoSection}>
        <div className={styles.insightsDemo}>
          <div className={styles.insightsDemoCopy}>
            <p className={styles.eyebrow}>Real-time insights and drop-off</p>
            <h2>See exactly where the journey loses momentum.</h2>
            <p>
              Watch performance update as people move through the experience. Compare engagement from card to card,
              identify the point where people leave and inspect the answers or paths around it.
            </p>
            <p>
              The dashboard turns a drop-off rate into something the team can act on: a specific screen, decision or
              piece of content to test next.
            </p>
          </div>
          <div className={styles.insightsVideoFrame}>
            <span>Live journey analysis</span>
            <LazyVideo
              className={styles.insightsVideo}
              src={isMobile ? insightsMedia.mobile : insightsMedia.desktop}
              autoplay
              poster={insightsMedia.poster}
              once
              title="Storycards real-time engagement and drop-off analytics"
            />
          </div>
        </div>
      </section>

      <section className={styles.diagnosticSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>From signal to diagnosis</p>
              <h2>Move from a performance change to the reason behind it.</h2>
            </div>
            <p>
              Start with the organization-wide picture, then narrow the analysis until the next product decision is
              clear.
            </p>
          </header>

          <div className={styles.diagnosticGrid}>
            {diagnosticSteps.map((item) => (
              <article key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>

          <div className={styles.optimizationLoop}>
            <div className={styles.loopCopy}>
              <p className={styles.eyebrow}>A continuous improvement loop</p>
              <h3>Measure in Analytics. Improve in Studio. Compare the result.</h3>
              <p>
                Analytics and creation stay inside the same operating platform. Teams can identify friction, update
                the design or flow, publish the approved change and compare what happened next.
              </p>
              <Link to={STUDIO_ROUTE} className={styles.textLink}>
                Explore Storycards Studio <span aria-hidden="true">→</span>
              </Link>
            </div>
            <ol className={styles.loopSteps}>
              <li><span>01</span><strong>Measure</strong><small>See behavior and outcomes.</small></li>
              <li><span>02</span><strong>Diagnose</strong><small>Find the step, path or audience.</small></li>
              <li><span>03</span><strong>Improve</strong><small>Refine content, design or logic.</small></li>
              <li><span>04</span><strong>Compare</strong><small>Measure the next version.</small></li>
            </ol>
          </div>
        </div>
      </section>

      <section className={styles.operatingSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Built for enterprise measurement</p>
              <h2>Use the dashboard. Keep your existing data stack.</h2>
            </div>
            <p>
              Storycards gives teams a native view of product performance while supporting the events, reports,
              systems and access model already used across the organization.
            </p>
          </header>

          <div className={styles.connectedAnalytics}>
            <div className={styles.connectedCopy}>
              <p className={styles.eyebrow}>Connected analytics</p>
              <h3>Keep Storycards behavior in the reporting environment your teams already use.</h3>
              <p>
                Connect a Storycards product to Google Analytics so activity inside the journey can appear alongside
                acquisition, website and campaign data. Teams can follow the path from traffic source to choice and
                outcome, without treating the interactive product as a separate data island.
              </p>
            </div>

            <div className={styles.eventFlow} aria-label="Storycards interactions flow into external analytics and business reporting">
              <div className={styles.eventSource}>
                <span>Storycards product</span>
                <strong>What people do inside the journey</strong>
                <ul>
                  <li>Card views</li>
                  <li>Answers</li>
                  <li>CTA clicks</li>
                  <li>Media engagement</li>
                </ul>
              </div>
              <div className={styles.eventConnector} aria-hidden="true">
                <span>Automatic events</span>
                <i />
                <span>Custom events</span>
              </div>
              <div className={styles.eventDestination}>
                <span>Google Analytics and your analytics stack</span>
                <strong>One view of source, behavior and outcome</strong>
                <p>Use the same reports, audiences and measurement model your organization already works with.</p>
              </div>
            </div>

            <div className={styles.connectedBenefits}>
              <article>
                <span>01</span>
                <h4>Key interactions are covered automatically</h4>
                <p>
                  Report card views, button and link clicks, answers, media engagement and searches without defining
                  each event from the beginning.
                </p>
              </article>
              <article>
                <span>02</span>
                <h4>Name the outcomes your business cares about</h4>
                <p>
                  Define custom events for moments such as a plan selection, qualified lead or completed form. Each
                  event keeps the Storycards product and card context behind it.
                </p>
              </article>
              <article>
                <span>03</span>
                <h4>Use native and external analytics together</h4>
                <p>
                  Diagnose product performance in Storycards, then use Google Analytics for cross-channel reporting,
                  audience analysis and the workflows already shared across your organization.
                </p>
              </article>
            </div>
          </div>

          <div className={styles.capabilityGrid}>
            {operatingCapabilities.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>

          <Link to={API_ROUTE} className={styles.apiLink}>
            See how Storycards data connects to your systems <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Make every interaction measurable.</h2>
          <div>
            <p>Talk with our enterprise team about the journeys, events and reporting needs your organization has.</p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
      </main>
    </PageLayout>
  );
};

export default Analytics;
